import { useCallback, useEffect, useRef, useState, type AnimationEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { selectHopTimusEnabled } from '@/containers/Login/AuthSelectors'
import { hopTimusActions } from '@/hoc/HopTimusButton/hopTimusSagas'
import { selectHopTimusDockStatus, selectHopTimusDockUrl } from '@/hoc/HopTimusButton/hopTimusSelectors'
import { dockAvailabilityChanged } from '@/hoc/HopTimusButton/hopTimusSlice'
import { closePulseWidget, registerDockFrame, releasePulseWidget } from '@/hoc/HopTimusButton/pulseLoader'
import useHopTimusShortcut from '@/hoc/HopTimusButton/useHopTimusShortcut'
import prefersReducedMotion from '@/shared/tools/prefersReducedMotion'
import { useRightPanelHopTimus } from '../rightPanelContext'
import type { HopTimusOpenLayout } from './styled-components'

const useHopTimusDock = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const enabled = useSelector(selectHopTimusEnabled)
  const status = useSelector(selectHopTimusDockStatus)
  const url = useSelector(selectHopTimusDockUrl)
  const { mode, afterFold, focusRequest, ask, close, detach, dock } = useRightPanelHopTimus()
  const frameRef = useRef<HTMLIFrameElement>(null)
  const wasOpen = useRef(false)
  const activatedForRequest = useRef(focusRequest)
  const frameLoaded = useRef(false)
  const focusPending = useRef(false)
  const handledFocusRequest = useRef(focusRequest)
  const [previousMode, setPreviousMode] = useState(mode)
  const [closingFrom, setClosingFrom] = useState<HopTimusOpenLayout | null>(null)

  if (mode !== previousMode) {
    const lastOpenLayout = previousMode !== 'closed' ? previousMode : null
    setPreviousMode(mode)
    setClosingFrom(mode === 'closed' && !prefersReducedMotion() ? lastOpenLayout : null)
  }

  const onFrameAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || mode !== 'closed') return
    setClosingFrom(null)
  }

  useEffect(() => {
    if (!enabled) return undefined
    closePulseWidget()
    dispatch(dockAvailabilityChanged(true))
    return () => {
      releasePulseWidget()
      dispatch(dockAvailabilityChanged(false))
    }
  }, [enabled, dispatch])

  // Activated on opening, not on page load: PULSE refuses an activation token older than 5 minutes.
  useEffect(() => {
    const isOpen = mode !== 'closed'
    const askedAgain = focusRequest !== activatedForRequest.current
    activatedForRequest.current = focusRequest
    if (isOpen && (!wasOpen.current || askedAgain) && (status === 'idle' || status === 'failed')) {
      dispatch({ type: hopTimusActions.ACTIVATE_DOCK, pageUrl: window.location.href })
    }
    wasOpen.current = isOpen
  }, [mode, status, focusRequest, dispatch])

  useEffect(() => {
    if (!url) return undefined
    frameLoaded.current = false
    registerDockFrame(frameRef.current)
    return () => registerDockFrame(null)
  }, [url])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return
      if (event.data?.type === 'pulse:close') close()
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [close])

  const focusFrame = useCallback(() => {
    frameRef.current?.focus()
    frameRef.current?.contentWindow?.focus()
  }, [])

  useEffect(() => {
    if (focusRequest === handledFocusRequest.current || mode === 'closed') return
    handledFocusRequest.current = focusRequest
    if (frameLoaded.current) {
      focusFrame()
      return
    }
    focusPending.current = true
  }, [focusRequest, mode, focusFrame])

  const onFrameLoad = () => {
    frameLoaded.current = true
    if (!focusPending.current) return
    focusPending.current = false
    focusFrame()
  }

  useHopTimusShortcut(enabled, ask)

  const floating = mode === 'floating'

  return {
    hidden: !enabled || (status === 'idle' && mode === 'closed'),
    mode,
    closingFrom,
    layout: mode === 'closed' ? closingFrom : mode,
    closing: mode === 'closed',
    afterFold,
    onFrameAnimationEnd,
    floating,
    ready: status === 'ready' && url !== null,
    failed: status === 'failed',
    url,
    frameRef,
    onFrameLoad,
    title: t('common:hop_timus_button'),
    message: t(status === 'failed' ? 'common:hop_timus_unavailable' : 'common:loading'),
    switchLabel: t(floating ? 'common:hop_timus_dock' : 'common:hop_timus_detach'),
    onSwitch: floating ? dock : detach,
  }
}

export default useHopTimusDock
